import React, { useContext, useEffect, useLayoutEffect, useState } from "react";
import Close from "../../../../../images/Close.svg";
import { Title } from "../../../../../stories/Title/Title";
import { Label } from "../../../../../stories/Label/Label";
import Input from "../../../../../stories/Input/Input";
import TextArea from "../../../../../stories/TextArea/TextArea";
import { LabelProps, TableHeaderProps } from "../../../../../utils/types";
import useAssetTableJson from "../../../../../json/useAssetTableJson";
import {
  GridAlignment,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import { TABLE_ROW_HEIGHT } from "../../../../../styles/Variables";
import { Operation, TableHeaders } from "../../../../../utils/Enum.types";
import { Button } from "../../../../../stories/Button/Button";
import { useLazyQuery } from "@apollo/client";
import { GetMrfTrns } from "./queries/queries";

import {
  AtsMrfTrnEdge,
  AtsMrfTrnOrderField,
  GetMrfTrnsResult,
  GetMrfTrnsVars,
  MrfTrnQueryType,
} from "./queries/types";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import {
  debouncedDelay,
  emptyMessage,
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
} from "../../../../../utils/constants";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { AppContext } from "../../../../../context/context";
import {
  getModifiedScrollHeight,
  toInputStandardDate,
} from "../../../../../utils/UtilFunctions";
import { IObjectKeys } from "../../../../../utils/formTypes";

import LoadingModal from "../../../../pages/LoadingModal";
import { ColumnVisibilityFor } from "../../../../../utils/Enum.Datagrid";
import useFormJson from "../../../../../json/useFormJson";
import { useDebounce } from "../../../../../customHooks/useDebounce";
interface Props {
  setModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface materialData extends IObjectKeys {
  mrfNo: string;
  materialReqFor: string;
  refNo: string;
  date: string;
  mrfRaisedBy: string;
  narration: string;
  mrfCopy: string;
}

interface totalsType {
  required_qty: number;
  approx_rate: number;
  taxable_amt: number;
  net_value: number;
}

const View = ({ setModalFlag }: Props) => {
  const { Purchase } = useAssetTableJson();
 
  const { state } = useContext(AppContext);
  const { customer_id } = useUrlParams();
  const [GetTransactions, { data, loading, fetchMore }] = useLazyQuery<
    GetMrfTrnsResult,
    GetMrfTrnsVars
  >(GetMrfTrns);
  const { viewMaterialRequestOrder } = useFormJson();
  const [message, setMessage] = useState(emptyMessage);

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);

  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData, debouncedDelay);
  const [items, setItems] = useState<AtsMrfTrnEdge[]>([]);

  const [rows, setRows] = useState<GridValidRowModel[]>([]);

  const [materialData, setMaterialData] = useState<materialData>({
    mrfNo: "",
    materialReqFor: "",
    refNo: "",
    date: "",
    mrfRaisedBy: "",
    narration: "",
    mrfCopy: "",
  });

  const [endCursor, setEndCursor] = useState<string | null>(null);

  const dynamicHeaders: TableHeaderProps[] = Purchase.Material.Add.filter(
    (header) => header.headerName !== TableHeaders.ACTION
  ).map((header) => ({
    headerName: header.headerName,
    field: header.field,
    headerAlign: header.headerAlign as GridAlignment,
    align: header.align as GridAlignment,
    flex: header.flex,
    hideable: header.hideable,
  }));
  const columns: GridColDef[] = [...dynamicHeaders];

  const getRow = () => {
    if (rows.length) {
      const totals = rows.reduce(
        (acc: totalsType, item) => {
          acc.required_qty = acc.required_qty + item.required_qty;
          acc.approx_rate = acc.approx_rate + item.approx_rate;
          acc.taxable_amt = acc.taxable_amt + item.taxable_amt;
          acc.net_value = acc.net_value + item.net_value;
          return acc;
        },
        {
          required_qty: 0,
          approx_rate: 0,
          taxable_amt: 0,
          net_value: 0,
        }
      );

      return [
        {
          id: [],
          item_code: [],
          uom: [],
          description: [],
          present_stock: "",
          required_qty: totals.required_qty,
          approx_rate: totals.approx_rate,
          taxable_amt: totals.taxable_amt,
          tax: [],
          net_value: totals.net_value,
          priority: [],
          need_before: [],
          prefered_vendor: [],
          used_for: [],
          remark: [],
          actions: [],
        },
      ];
    }
    return [];
  };

  const pinnedRows = {
    bottom: getRow(),
  };

  const getCellClassName = (params: GridCellParams) => {
    const pinnedRowIds = pinnedRows.bottom
      ? pinnedRows.bottom.map((row) => row.id)
      : [];

    const isPinnedRow = pinnedRowIds.includes(params.row.id);

    if (isPinnedRow) {
      switch (params.field) {
        case "required_qty":
        case "approx_rate":
        case "taxable_amt":
        case "net_value":
          return "total-count";
        default:
          return "total";
      }
    }

    return "";
  };

  useEffect(() => {
    if (customer_id && state.ActiveFinYr)
      GetTransactions({
        variables: {
          after: null,
          customerId: customer_id,
          finYrId: state.ActiveFinYr.id,
          first: ROWS_PER_PAGE,
          mrfId: state.mrfId,
          queryType: MrfTrnQueryType.ALL_ITEMS,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsMrfTrnOrderField.SL_NO,
          },
          name: debouncedSearch,
          
        },
      }).then(({ data }) => {
        if (data && data.GetMrfTrns) {
          if (data.GetMrfTrns.edges.length) {
            const mrfDetails = data.GetMrfTrns.edges[0].node;
            setMaterialData({
              date: toInputStandardDate(mrfDetails.mrf.mrfDate),
              materialReqFor: mrfDetails.mrf.subsidiary.name,
              mrfNo: mrfDetails.mrf.mrfNo,
              refNo: mrfDetails.mrf.refNo,
              mrfCopy: "",
              mrfRaisedBy: `${mrfDetails.mrf.raisedByUserDetails.name}`,
              narration: mrfDetails.mrf.remarks,
            });
          }
        }
      });
  }, [GetTransactions, customer_id, state.ActiveFinYr, debouncedSearch]);

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetMrfTrns.edges;
              const pageInfo = fetchMoreResult.GetMrfTrns.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetMrfTrns.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetMrfTrns: {
                  edges: [...items, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetMrfTrns.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable && rows.length)
      scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [rows]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetMrfTrns.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = items.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItems(updatedNewData);
        setRows(
          updatedNewData.map(({ node }, index) => ({
            id: index + 1,
            item_code: node.item.code,
            uom: node.item.uom,
            description: node.item.desc,
            present_stock: node.presentStk,
            required_qty: node.reqQty,
            approx_rate: node.approxRate,
            taxable_amt: node.taxable,
            tax: node.tax,
            net_value: node.approxValue,
          }))
        );
      } else {
        setItems(newData);
        setRows(
          newData.map(({ node }, index) => ({
            id: index + 1,
            item_code: node.item.code,
            uom: node.item.uom,
            description: node.item.desc,
            present_stock: node.presentStk,
            required_qty: node.reqQty,
            approx_rate: node.approxRate,
            taxable_amt: node.taxable,
            tax: node.tax,
            net_value: node.approxValue,
          }))
        );
      }
      setEndCursor(data.GetMrfTrns.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  useEffect(() => {
    const savedModel = localStorage.getItem(
      ColumnVisibilityFor.MATERIAL_REQUEST_DETAILS
    );
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, []);

  const handleColumnVisibilityChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(
      ColumnVisibilityFor.MATERIAL_REQUEST_DETAILS,
      JSON.stringify(newModel)
    );
  };
  return (
    <>
      <div className="material-view">
        <div className="material-view__title">
          <Title>View Material Request Details</Title>
          <img src={Close} alt="" onClick={() => setModalFlag(false)} />
        </div>
        <div className="material-view__filterblock row g-0">
          <div className="col material-view__filterblock--frame">
            <div className="label-grid">
              {viewMaterialRequestOrder.leftForm.map((data: LabelProps) => {
                return (
                  <>
                    <Label>{data.labelName}</Label>
                    <Input disabled value={materialData[data.inputName]} />
                  </>
                );
              })}
            </div>
          </div>
          <div className="col material-view__filterblock--frame">
            <div className="material-view__filterblock--grid">
              <Label>Date</Label>
              <Input type="date" disabled value={materialData.date} />
            </div>
          </div>
        </div>
        <div className="material-view__tableblock">
          <StyledDatagrid
            columns={columns}
            rows={rows}
            rowHeight={TABLE_ROW_HEIGHT}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityChange}
            initialState={{
              columns: {
                columnVisibilityModel: columnVisibilityModel,
              },
            }}
            disableRowSelectionOnClick
            hideFooter
            pinnedRows={pinnedRows}
            getCellClassName={getCellClassName}
          />
        </div>
        <div className="material__add--datablock">
          <div className="row g-0">
            <div className="col">
              <div className="label-grid">
                <Label>MRF Raised By</Label>
                <Input disabled value={materialData.mrfRaisedBy} />
              </div>
            </div>
            <div className="col">
              <div className="label-grid">
                <Label>MRF Copy</Label>
                <Input disabled />
              </div>{" "}
            </div>
          </div>
          <div className="material__add--datablock--grid">
            <Label>Narration</Label>
            <TextArea rows={3} disabled />
          </div>
        </div>
        <Button mode="cancel" onClick={() => setModalFlag(false)} />
      </div>
      <LoadingModal flag={loading} />
    </>
  );
};

export default View;
